import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Link2, Search, X, Clock, User as UserIcon, Camera, Receipt, Ban } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContext';
import { Modal } from '../components/Modal';
import { ApprovazioniService, IncaricoService, PraticaService, ServizioFiduciarioService } from '../services/apiService';
import { emitApprovazioniChanged, subscribeApprovazioniRefreshRequested } from '../utils/approvazioniEvents';
import { getClientFullLabel } from '../utils/clientDisplay';

// Tab "Da Abbinare" di /approvazioni: incarichi orfani (URGENTE_SENZA_MANDATO
// senza pratica) creati dal collaboratore per clienti nuovi. Il titolare li
// abbina a un mandato o li revoca, anche se il report non è consegnato o sono in corso.
// L'abbinamento a un servizio (turno generato) richiede invece attività chiusa e report inviato.

interface OrphanIncarico {
    id: number;
    soggettoNome?: string;
    dataInizio?: string;
    dataFine?: string;
    collaboratore?: { id: number; name?: string; surname?: string };
    media?: any[];
    sessioni?: any[];
    relazioni?: any[];
}

const fmtDateTime = (iso?: string) =>
    iso ? new Date(iso).toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    }) : '—';

const oreLavorate = (inc: OrphanIncarico): number => {
    if (!inc.sessioni?.length) return 0;
    const ms = inc.sessioni.reduce((acc: number, s: any) => {
        if (!s.startTime || !s.endTime) return acc;
        return acc + (new Date(s.endTime).getTime() - new Date(s.startTime).getTime());
    }, 0);
    return Math.round(ms / 3_600_000 * 10) / 10;
};

const speseCount = (inc: OrphanIncarico): number => {
    if (!inc.relazioni?.length) return 0;
    return inc.relazioni.reduce((n: number, r: any) => n + (r.speseEspanso?.length || r.spese?.length || 0), 0);
};

// Nomi dei soggetti investigati dichiarati sul mandato; stessa logica di
// IncaricoController.extractSoggettiDaPratica lato backend.
const extractSoggetti = (pratica: any): string[] => {
    const raw = pratica?.datiAggiuntiviJson;
    if (!raw) return [];
    let dati: any = raw;
    if (typeof raw === 'string') {
        try { dati = JSON.parse(raw); } catch { return []; }
    }
    if (Array.isArray(dati?.soggetti)) {
        const out = dati.soggetti
            .map((s: any) => (typeof s?.nome === 'string' ? s.nome.trim() : ''))
            .filter((n: string) => n.length > 0);
        if (out.length > 0) return out;
    }
    // Vecchio formato a soggetto singolo
    const legacy = (dati?.soggetto_nome || dati?.soggetto_denominazione || '').toString().trim();
    return legacy ? [legacy] : [];
};

export const IncarichiDaAbbinareView: React.FC = () => {
    const { currentSedeId } = useAuth();
    const { addToast } = useToast();
    const [items, setItems] = useState<OrphanIncarico[]>([]);
    const [loading, setLoading] = useState(true);
    const [abbinaTarget, setAbbinaTarget] = useState<OrphanIncarico | null>(null);
    const [revocaTarget, setRevocaTarget] = useState<OrphanIncarico | null>(null);
    const [revocando, setRevocando] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await ApprovazioniService.getIncarichiDaAbbinare(currentSedeId || 'ALL');
            setItems(Array.isArray(list) ? list : []);
        } catch {
            addToast('Errore caricamento incarichi da abbinare', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentSedeId, addToast]);

    // La revoca usa l'endpoint standard /revoca (stato REVOCATO): l'incarico sparisce
    // dalla vista collaboratore e dalla lista "Da Abbinare", che ignora gli stati terminali.
    const handleRevoca = useCallback(async (inc: OrphanIncarico) => {
        setRevocando(true);
        try {
            await IncaricoService.revoca(inc.id);
            addToast('Incarico revocato', 'success');
            setRevocaTarget(null);
            load();
            emitApprovazioniChanged();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Errore revoca incarico', 'error');
        } finally {
            setRevocando(false);
        }
    }, [addToast, load]);

    useEffect(() => { load(); }, [load]);
    // Ricarica quando si preme "Aggiorna" nel container, così la lista segue il badge.
    useEffect(() => subscribeApprovazioniRefreshRequested(() => { load(); }), [load]);

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto px-6 py-12 text-center text-slate-400">
                <Loader2 className="animate-spin mx-auto mb-2" size={28} />
                Caricamento…
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="max-w-6xl mx-auto px-6 py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-500 mx-auto flex items-center justify-center mb-3">
                    <Link2 size={28}/>
                </div>
                <p className="text-sm font-bold text-slate-700">Nessun incarico da abbinare</p>
                <p className="text-xs text-slate-500 mt-1">Gli incarichi urgenti aperti senza mandato compariranno qui non appena il collaboratore li crea, anche prima della consegna del report.</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-6 pb-12">
            <div className="space-y-3">
                {items.map(inc => {
                    const collabName = inc.collaboratore
                        ? [inc.collaboratore.name, inc.collaboratore.surname].filter(Boolean).join(' ')
                        : '—';
                    const ore = oreLavorate(inc);
                    const nMedia = inc.media?.length || 0;
                    const nSpese = speseCount(inc);

                    return (
                        <div
                            key={inc.id}
                            className="bg-white border-2 border-amber-200 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                        Da Abbinare
                                    </span>
                                    <span className="text-[10px] text-slate-400 font-bold">#{inc.id}</span>
                                </div>
                                <h3 className="font-bold text-slate-900 text-base truncate">
                                    {inc.soggettoNome || `Incarico #${inc.id}`}
                                </h3>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] font-semibold text-slate-500">
                                    <span className="flex items-center gap-1"><UserIcon size={11}/> {collabName}</span>
                                    <span className="flex items-center gap-1"><Clock size={11}/> {fmtDateTime(inc.dataInizio)}</span>
                                    {ore > 0 && <span className="text-emerald-700">{ore}h lavorate</span>}
                                    {nMedia > 0 && <span className="flex items-center gap-1"><Camera size={11}/> {nMedia}</span>}
                                    {nSpese > 0 && <span className="flex items-center gap-1"><Receipt size={11}/> {nSpese} spese</span>}
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                <button
                                    onClick={() => setAbbinaTarget(inc)}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-sm transition-colors"
                                >
                                    <Link2 size={14}/> Abbina
                                </button>
                                <button
                                    onClick={() => setRevocaTarget(inc)}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-red-200 hover:bg-red-50 text-red-700 text-xs font-black uppercase tracking-wider rounded-xl shadow-sm transition-colors"
                                >
                                    <Ban size={14}/> Revoca
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {abbinaTarget && (
                <AbbinaModal
                    incarico={abbinaTarget}
                    onClose={() => setAbbinaTarget(null)}
                    onDone={() => { setAbbinaTarget(null); load(); emitApprovazioniChanged(); }}
                />
            )}

            {/* Conferma revoca: niente input libero, solo OK/Annulla. */}
            {revocaTarget && (
                <Modal
                    title="Revocare l'incarico?"
                    onClose={() => !revocando && setRevocaTarget(null)}
                >
                    <div className="space-y-4">
                        <p className="text-sm text-slate-700 leading-relaxed">
                            L'incarico <strong className="text-slate-900">"{revocaTarget.soggettoNome || `#${revocaTarget.id}`}"</strong> verrà
                            portato in stato <strong>REVOCATO</strong>. Sparirà sia da questa coda sia dalla vista del
                            collaboratore. L'operazione è tracciata in audit log e non è automaticamente reversibile.
                        </p>
                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setRevocaTarget(null)}
                                disabled={revocando}
                                className="flex-1 py-3 font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
                            >Annulla</button>
                            <button
                                type="button"
                                onClick={() => handleRevoca(revocaTarget)}
                                disabled={revocando}
                                className="flex-[2] py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {revocando ? <Loader2 size={16} className="animate-spin"/> : <Ban size={16}/>}
                                Conferma revoca
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

interface AbbinaModalProps {
    incarico: OrphanIncarico;
    onClose: () => void;
    onDone: () => void;
}

// Prima scelta della modale: mandato (abbinamento classico) o servizio (turno generato).
const AbbinaModal: React.FC<AbbinaModalProps> = (props) => {
    const [tipo, setTipo] = useState<'mandato' | 'servizio'>('mandato');
    const [busy, setBusy] = useState(false);
    const { incarico, onClose } = props;
    return (
        <Modal title={`Abbina "${incarico.soggettoNome || `#${incarico.id}`}"`} onClose={() => !busy && onClose()}>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl">
                    {(['mandato', 'servizio'] as const).map(t => (
                        <button
                            key={t}
                            type="button"
                            disabled={busy}
                            onClick={() => setTipo(t)}
                            className={`py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-colors ${
                                tipo === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {t === 'mandato' ? 'A un mandato' : 'A un servizio'}
                        </button>
                    ))}
                </div>
                {tipo === 'mandato'
                    ? <AbbinaMandatoForm {...props} onBusy={setBusy} />
                    : <AbbinaServizioForm {...props} onBusy={setBusy} />}
            </div>
        </Modal>
    );
};

const hhmm = (t?: string) => (t || '').slice(0, 5);

const oreTra = (inizio: string, fine: string): number | null => {
    if (!/^\d{2}:\d{2}$/.test(inizio) || !/^\d{2}:\d{2}$/.test(fine)) return null;
    const [h1, m1] = inizio.split(':').map(Number);
    const [h2, m2] = fine.split(':').map(Number);
    let min = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (min <= 0) min += 24 * 60;
    return Math.round(min / 6) / 10;
};

const FONTE_ORE: Record<string, string> = {
    APPROVATE: 'ore cliente già approvate',
    REPORT: 'orari di indagine del report',
    GPS: 'ore GPS (il report non ha orari di indagine)',
};

// L'urgente diventa un turno già svolto: start/stop (con le foto km), report, spese e note
// passano al turno, foto e video di prova vengono eliminati. Data e orari sono proposti e
// modificabili. Si propongono i servizi attivi a cui il collaboratore è abilitato; per gli
// altri serve la sua tariffa, che viene aggiunta al servizio.
const AbbinaServizioForm: React.FC<AbbinaModalProps & { onBusy: (b: boolean) => void }> = ({ incarico, onClose, onDone, onBusy }) => {
    const { addToast } = useToast();
    const { currentSedeId } = useAuth();
    const [servizi, setServizi] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [servizioId, setServizioId] = useState<number | null>(null);
    const [proposta, setProposta] = useState<any>(null);
    const [errore, setErrore] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [dataTurno, setDataTurno] = useState('');
    const [oraInizio, setOraInizio] = useState('');
    const [oraFine, setOraFine] = useState('');
    const [confermaMedia, setConfermaMedia] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [mostraTutti, setMostraTutti] = useState(false);
    const [tariffa, setTariffa] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const sedeId = typeof currentSedeId === 'number' ? currentSedeId : undefined;
                const [p, res] = await Promise.all([
                    IncaricoService.getPropostaTurno(incarico.id),
                    ServizioFiduciarioService.getAll(sedeId !== undefined ? { sedeId } : undefined),
                ]);
                if (cancelled) return;
                setProposta(p);
                setDataTurno(p?.dataTurno || '');
                setOraInizio(hhmm(p?.oraInizio));
                setOraFine(hhmm(p?.oraFine));
                const list = Array.isArray(res?.data) ? res.data : [];
                setServizi(list.filter((s: any) => !s.stato || s.stato === 'Attivo'));
            } catch (e: any) {
                if (!cancelled) setErrore(e?.response?.data?.message || 'Impossibile preparare il turno');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [incarico.id, currentSedeId]);

    const abilitati = useMemo(
        () => new Set<number>(Array.isArray(proposta?.serviziAbilitatiIds) ? proposta.serviziAbilitatiIds : []),
        [proposta]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return servizi
            .filter(s => mostraTutti || abilitati.has(s.id))
            .filter(s => !q
                || (s.nome || '').toLowerCase().includes(q)
                || (s.clientName || '').toLowerCase().includes(q)).slice(0, 30);
    }, [servizi, search, mostraTutti, abilitati]);

    const nMedia: number = proposta?.numeroMedia ?? (incarico.media?.length || 0);
    const nFotoKm: number = proposta?.numeroFotoKm ?? 0;
    const oreCliente = oreTra(oraInizio, oraFine);
    const notturno = oreCliente !== null && oraFine <= oraInizio;
    const serveTariffa = !!servizioId && !abilitati.has(servizioId);
    const tariffaNum = Number(tariffa.replace(',', '.'));
    const tariffaValida = tariffa.trim() !== '' && Number.isFinite(tariffaNum) && tariffaNum >= 0;
    const canSubmit = !!servizioId && !!dataTurno && oreCliente !== null && oraInizio !== oraFine
        && (nMedia === 0 || confermaMedia) && (!serveTariffa || tariffaValida);

    const handleConfirm = async () => {
        if (!servizioId) return;
        setSubmitting(true);
        onBusy(true);
        try {
            await IncaricoService.convertiInTurno(incarico.id, {
                servizioId, dataTurno, oraInizio, oraFine,
                confermaEliminazioneMedia: confermaMedia,
                tariffaOraria: serveTariffa ? tariffaNum : undefined,
            });
            addToast('Incarico abbinato al servizio: turno creato', 'success');
            onDone();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Errore abbinamento al servizio', 'error');
        } finally {
            setSubmitting(false);
            onBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 text-center text-slate-400">
                <Loader2 className="animate-spin mx-auto mb-1" size={20}/>
                <span className="text-xs">Preparazione del turno…</span>
            </div>
        );
    }
    if (errore) {
        return (
            <div className="space-y-4">
                <p className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-xl p-4">{errore}</p>
                <button type="button" onClick={onClose} className="w-full py-3 font-bold text-slate-500 hover:bg-slate-100 rounded-xl">Chiudi</button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input
                    type="text"
                    placeholder="Cerca servizio o cliente…"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 ring-blue-400 text-sm"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoFocus
                />
            </div>
            <div className="border border-slate-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-slate-100">
                {filtered.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500">
                        {mostraTutti || search.trim()
                            ? 'Nessun servizio attivo trovato.'
                            : 'Il collaboratore non è abilitato a nessun servizio attivo.'}
                    </div>
                ) : filtered.map((s: any) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => setServizioId(s.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors ${servizioId === s.id ? 'bg-blue-100' : ''}`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-slate-900 truncate">{s.nome || `Servizio #${s.id}`}</span>
                            {!abilitati.has(s.id) && (
                                <span className="flex-shrink-0 text-[10px] font-bold text-amber-800 bg-amber-100 rounded px-1.5 py-0.5">Non abilitato</span>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{s.clientName || '—'}</div>
                    </button>
                ))}
            </div>
            <label className="flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
                <input
                    type="checkbox"
                    checked={mostraTutti}
                    onChange={e => {
                        setMostraTutti(e.target.checked);
                        if (!e.target.checked && servizioId && !abilitati.has(servizioId)) setServizioId(null);
                    }}
                />
                Mostra anche i servizi a cui il collaboratore non è abilitato
            </label>
            {serveTariffa && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                    <p className="text-[12px] text-amber-900 leading-relaxed">
                        Il collaboratore non è abilitato a questo servizio: indica la sua tariffa oraria,
                        verrà aggiunta al servizio insieme al turno.
                    </p>
                    <label className="text-[11px] font-bold text-slate-600 block">Tariffa oraria collaboratore (€/h)
                        <input
                            type="text"
                            inputMode="decimal"
                            value={tariffa}
                            onChange={e => setTariffa(e.target.value)}
                            placeholder="es. 12,50"
                            className="mt-1 w-full px-2 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                        />
                    </label>
                </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Turno da creare</div>
                <div className="grid grid-cols-3 gap-2">
                    <label className="text-[11px] font-bold text-slate-600">Data
                        <input type="date" value={dataTurno} onChange={e => setDataTurno(e.target.value)}
                               className="mt-1 w-full px-2 py-2 border border-slate-200 rounded-lg text-sm bg-white"/>
                    </label>
                    <label className="text-[11px] font-bold text-slate-600">Inizio
                        <input type="time" value={oraInizio} onChange={e => setOraInizio(e.target.value)}
                               className="mt-1 w-full px-2 py-2 border border-slate-200 rounded-lg text-sm bg-white"/>
                    </label>
                    <label className="text-[11px] font-bold text-slate-600">Fine
                        <input type="time" value={oraFine} onChange={e => setOraFine(e.target.value)}
                               className="mt-1 w-full px-2 py-2 border border-slate-200 rounded-lg text-sm bg-white"/>
                    </label>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                    Ore al cliente: <strong>{oreCliente ?? '—'} h</strong>{notturno && ' (fine il giorno dopo)'}.
                    {proposta && <> Proposta da start {hhmm(proposta.oraInizio)} + {proposta.oreCliente} h ({FONTE_ORE[proposta.fonteOreCliente] || proposta.fonteOreCliente}).</>}
                </p>
                {proposta && (
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                        Al collaboratore: ore GPS reali ({proposta.oreGps} h){proposta.oreExtra > 0 && <> + {proposta.oreExtra} h extra</>}.
                        Report, km e spese passano al turno così come sono.
                        {nFotoKm > 0 && <> Le foto km diventano le foto di start e stop del turno.</>}
                    </p>
                )}
            </div>

            {nMedia > 0 && (
                <label className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-4 cursor-pointer">
                    <input type="checkbox" checked={confermaMedia} onChange={e => setConfermaMedia(e.target.checked)} className="mt-0.5 accent-red-600"/>
                    <span className="text-[12px] text-red-800 leading-relaxed">
                        L'incarico ha <strong>{nMedia} media</strong> (foto e video di prova): abbinandolo a un servizio
                        <strong> verranno eliminati definitivamente</strong>. Confermo l'eliminazione.
                    </span>
                </label>
            )}

            <div className="flex gap-2 pt-1">
                <button type="button" onClick={onClose} disabled={submitting}
                        className="flex-1 py-3 font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors">Annulla</button>
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={submitting || !canSubmit}
                    className="flex-[2] py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {submitting ? <Loader2 size={16} className="animate-spin"/> : <Link2 size={16}/>}
                    Crea turno e abbina
                </button>
            </div>
        </div>
    );
};

const AbbinaMandatoForm: React.FC<AbbinaModalProps & { onBusy: (b: boolean) => void }> = ({ incarico, onClose, onDone, onBusy }) => {
    const { addToast } = useToast();
    const { currentSedeId } = useAuth();
    const [search, setSearch] = useState('');
    const [pratiche, setPratiche] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // Con più soggetti il titolare deve scegliere a chi assegnare l'incarico.
    // Si calcola dal datiAggiuntiviJson già nel DTO, senza chiamate extra.
    const [soggettoSelezionato, setSoggettoSelezionato] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await PraticaService.getAll({
                    search: search.trim() || undefined,
                    sedeId: currentSedeId && currentSedeId !== 'ALL' ? Number(currentSedeId) : undefined,
                    size: 30,
                });
                if (cancelled) return;
                const list = res?.data?.content ?? res?.content ?? res?.data ?? [];
                setPratiche(Array.isArray(list) ? list : []);
            } catch {
                if (!cancelled) setPratiche([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [search, currentSedeId]);

    const filtered = useMemo(() => pratiche.slice(0, 30), [pratiche]);

    // Cambiando mandato si azzera il soggetto (auto-impostato se ce n'è uno solo).
    const selectedPratica = useMemo(
        () => filtered.find((p: any) => p.id === selectedId) || null,
        [filtered, selectedId]
    );
    const soggettiMandato = useMemo(
        () => extractSoggetti(selectedPratica),
        [selectedPratica]
    );
    useEffect(() => {
        if (soggettiMandato.length === 1) setSoggettoSelezionato(soggettiMandato[0]);
        else setSoggettoSelezionato(null);
    }, [selectedId, soggettiMandato]);

    const canSubmit = !!selectedId
        && (soggettiMandato.length <= 1 || !!soggettoSelezionato);

    const handleConfirm = async () => {
        if (!selectedId) { addToast('Seleziona un mandato', 'error'); return; }
        if (soggettiMandato.length > 1 && !soggettoSelezionato) {
            addToast('Scegli il soggetto a cui assegnare l\'incarico', 'error');
            return;
        }
        setSubmitting(true);
        onBusy(true);
        try {
            await IncaricoService.abbina(
                incarico.id,
                selectedId,
                soggettoSelezionato || undefined,
            );
            addToast('Incarico abbinato', 'success');
            onDone();
        } catch (e: any) {
            // Il BE risponde 409 se il mandato ha più soggetti e non ne abbiamo indicato
            // uno (es. mandato appena modificato): aggiorniamo la UI con soggettiDisponibili.
            const status = e?.response?.status;
            const fallbackList = e?.response?.data?.data?.soggettiDisponibili;
            if (status === 409 && Array.isArray(fallbackList) && fallbackList.length > 0) {
                addToast('Il mandato richiede la scelta del soggetto', 'error');
                // Aggiorna la cache della pratica selezionata con i nuovi soggetti.
                setPratiche(prev => prev.map((p: any) =>
                    p.id === selectedId
                        ? { ...p, datiAggiuntiviJson: JSON.stringify({
                            ...(typeof p.datiAggiuntiviJson === 'string'
                                ? JSON.parse(p.datiAggiuntiviJson || '{}')
                                : (p.datiAggiuntiviJson || {})),
                            soggetti: fallbackList.map((nome: string) => ({ nome })),
                        }) }
                        : p
                ));
                setSoggettoSelezionato(null);
            } else {
                addToast(e?.response?.data?.message || 'Errore abbinamento', 'error');
            }
        } finally {
            setSubmitting(false);
            onBusy(false);
        }
    };

    return (
            <div className="space-y-4">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input
                        type="text"
                        placeholder="Cerca mandato per nome o cliente…"
                        className="w-full pl-9 pr-9 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 ring-blue-400 text-sm"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        autoFocus
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                        >
                            <X size={14}/>
                        </button>
                    )}
                </div>

                <div className="border border-slate-200 rounded-xl max-h-80 overflow-y-auto divide-y divide-slate-100">
                    {loading ? (
                        <div className="p-6 text-center text-slate-400">
                            <Loader2 className="animate-spin mx-auto mb-1" size={20}/>
                            <span className="text-xs">Ricerca…</span>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-6 text-center text-xs text-slate-500">
                            Nessun mandato trovato. Crea prima il mandato dalla pagina Mandati, poi torna qui.
                        </div>
                    ) : filtered.map((p: any) => {
                        // Azienda: "Referente (Ragione Sociale)", così il titolare riconosce subito
                        // il referente. Supporta sia p.client.name che p.client.nome.
                        const clientShape = p.client ? {
                            ...p.client,
                            nome: p.client.nome || p.client.name,
                        } : null;
                        const clientName = getClientFullLabel(clientShape) || '—';
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => setSelectedId(p.id)}
                                className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors ${
                                    selectedId === p.id ? 'bg-blue-100' : ''
                                }`}
                            >
                                <div className="font-bold text-sm text-slate-900 truncate">{p.name || `Mandato #${p.id}`}</div>
                                <div className="text-[11px] text-slate-500 truncate">{clientName}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Scelta soggetto, dopo il mandato: con uno solo è automatica, con più è obbligatoria. */}
                {selectedPratica && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
                            Soggetto investigato di questo mandato
                        </div>
                        {soggettiMandato.length === 1 && (
                            <p className="text-[12px] text-slate-700 leading-relaxed">
                                Il mandato ha un solo soggetto: l'incarico verrà attribuito a
                                <span className="font-bold text-slate-900"> "{soggettiMandato[0]}"</span>.
                            </p>
                        )}
                        {soggettiMandato.length > 1 && (
                            <>
                                <p className="text-[11px] text-slate-600 mb-3">
                                    Il mandato ha {soggettiMandato.length} soggetti: scegli a chi assegnare l'incarico (i media seguiranno).
                                </p>
                                <div className="space-y-1.5">
                                    {soggettiMandato.map(nome => (
                                        <label
                                            key={nome}
                                            className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                                                soggettoSelezionato === nome
                                                    ? 'bg-blue-50 border-blue-300'
                                                    : 'bg-white border-slate-200 hover:bg-slate-50'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="soggettoAbbina"
                                                checked={soggettoSelezionato === nome}
                                                onChange={() => setSoggettoSelezionato(nome)}
                                                className="accent-blue-600"
                                            />
                                            <span className="text-sm font-semibold text-slate-800">{nome}</span>
                                        </label>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div className="flex gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="flex-1 py-3 font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
                    >Annulla</button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={submitting || !canSubmit}
                        className="flex-[2] py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin"/> : <Link2 size={16}/>}
                        Conferma abbinamento
                    </button>
                </div>
            </div>
    );
};

export default IncarichiDaAbbinareView;
